/**
 * **The hub card** — one card per page a hub opens (C11's Settings hub, FR-A8
 * §1; and since Wave 14 the Website hub too, C13 §10.5 item 1).
 *
 * Factored out of `routes/Firm/settings/Hub.tsx` so both hubs draw the same
 * card: an icon, a title, what the page is for, and **one line of what is set
 * now** under a rule — a skeleton while it loads, *Open to see what’s set*
 * when its read failed, never a stale or invented line. Hover lifts it.
 *
 * `HubGroup` is the labelled section a hub groups its cards under, and
 * `HubRow` a single full-width link in the same manner (the Settings hub's
 * row to *Your website*, Profession's row to *What you may say*).
 */

import { Skeleton, TONE_TEXT } from '@fiveox/ui'
import type { LucideIcon } from 'lucide-react'
import { ChevronRight, CircleAlert, CircleCheck, CircleDashed } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router'

import type { SummaryLine } from '../lib/api/firm'

export function LineIcon({ tone }: { tone: SummaryLine['tone'] }) {
  if (tone === 'attention') {
    return <CircleAlert size={14} className={`shrink-0 ${TONE_TEXT.warning}`} aria-hidden />
  }
  if (tone === 'unset') {
    return <CircleDashed size={14} className="text-muted-foreground shrink-0" aria-hidden />
  }
  return <CircleCheck size={14} className="text-primary shrink-0" aria-hidden />
}

export interface HubCardProps {
  /** `data-card` and `data-summary` — what a test reads. */
  cardKey: string
  href: string
  icon: LucideIcon
  title: string
  description: string
  line: SummaryLine | null
  loading: boolean
}

export function HubCard({ cardKey, href, icon: Icon, title, description, line, loading }: HubCardProps) {
  return (
    <Link
      to={href}
      data-card={cardKey}
      className="group border-border hover:border-primary/40 focus-visible:ring-primary/40 flex h-full min-w-0 flex-col rounded-2xl border bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:ring-2 focus-visible:outline-none"
    >
      <div className="flex items-start gap-4">
        <span className="bg-primary/10 text-primary group-hover:bg-primary flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors group-hover:text-white">
          <Icon size={20} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-foreground text-base font-semibold">{title}</h3>
            <ChevronRight
              size={18}
              className="text-muted-foreground group-hover:text-primary shrink-0 transition-transform group-hover:translate-x-0.5"
              aria-hidden
            />
          </div>
          <p className="text-muted-foreground mt-1 text-sm">{description}</p>
        </div>
      </div>
      <div className="border-border mt-auto pt-4">
        <div className="border-border border-t pt-3">
          {loading ? (
            <Skeleton className="h-4 w-2/3" />
          ) : line ? (
            <p
              data-summary={cardKey}
              className={`flex items-center gap-2 text-sm ${
                line.tone === 'unset' ? 'text-muted-foreground' : 'text-foreground'
              }`}
            >
              <LineIcon tone={line.tone} />
              <span className="min-w-0 truncate">{line.line}</span>
            </p>
          ) : (
            <p className="text-muted-foreground text-sm">Open to see what’s set</p>
          )}
        </div>
      </div>
    </Link>
  )
}

/** A hub's labelled group: a small caps heading, one line, and the grid. */
export function HubGroup({
  id,
  title,
  description,
  children,
  columns = 3,
}: {
  /** Prefixed ids are the caller's — `settings-practice`, `site-look`. */
  id: string
  title: string
  description?: string
  children: ReactNode
  /** Cards across at the widest. 3 by default; 2 for a group of two or four,
   *  which would otherwise leave a hole. */
  columns?: 2 | 3
}) {
  return (
    <section aria-labelledby={id} className="space-y-3">
      <div>
        <h2 id={id} className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
          {title}
        </h2>
        {description && <p className="text-muted-foreground text-sm">{description}</p>}
      </div>
      <div
        className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${columns === 3 ? 'xl:grid-cols-3' : ''}`}
      >
        {children}
      </div>
    </section>
  )
}

/** One full-width link in the hub card's manner — a pointer to another hub or
 *  page, not a page of this one. */
export function HubRow({
  rowKey,
  href,
  icon: Icon,
  title,
  description,
}: {
  rowKey: string
  href: string
  icon: LucideIcon
  title: string
  description: ReactNode
}) {
  return (
    <Link
      to={href}
      data-row={rowKey}
      className="group border-border hover:border-primary/40 focus-visible:ring-primary/40 flex min-w-0 items-center gap-4 rounded-2xl border bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:ring-2 focus-visible:outline-none sm:p-5"
    >
      <span className="bg-primary/10 text-primary group-hover:bg-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors group-hover:text-white">
        <Icon size={18} aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-foreground block text-sm font-semibold">{title}</span>
        <span className="text-muted-foreground block text-sm">{description}</span>
      </span>
      <ChevronRight
        size={18}
        className="text-muted-foreground group-hover:text-primary shrink-0 transition-transform group-hover:translate-x-0.5"
        aria-hidden
      />
    </Link>
  )
}
